import { useEffect, useRef, useState } from 'preact/hooks';

const THRESHOLD = 80;   // px to pull before triggering refresh
const MAX_PULL = 120;   // max visual displacement
const RESISTANCE = 0.4; // pull resistance factor

/**
 * Pull-to-refresh hook for PWA standalone mode.
 * Attaches to the app container and triggers window.location.reload()
 * when the user swipes down from the top of a scroll-at-top container.
 */
export function usePullToRefresh() {
  const [pulling, setPulling] = useState(false);
  const [pullDistance, setPullDistance] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const startY = useRef(0);
  const currentY = useRef(0);
  const isPulling = useRef(false);
  const refreshingRef = useRef(false);

  useEffect(() => {
    const isMobile = 'ontouchstart' in window;
    const isStandalone =
      window.matchMedia('(display-mode: standalone)').matches ||
      (navigator as any).standalone === true;

    if (!isMobile && !isStandalone) return;

    function isAtTop(): boolean {
      const chatArea = document.querySelector('.chat-area');
      if (chatArea && chatArea.scrollTop > 5) return false;
      if (document.documentElement.scrollTop > 5) return false;
      if (document.body.scrollTop > 5) return false;
      return true;
    }

    function isComposeActive(): boolean {
      const ae = document.activeElement;
      if (ae && (ae.tagName === 'TEXTAREA' || ae.tagName === 'INPUT')) return true;
      return false;
    }

    function onTouchStart(e: TouchEvent) {
      if (refreshingRef.current) return;
      startY.current = e.touches[0].clientY;
      currentY.current = e.touches[0].clientY;
      isPulling.current = false;
    }

    function onTouchMove(e: TouchEvent) {
      if (refreshingRef.current) return;
      if (isComposeActive()) return;
      const y = e.touches[0].clientY;
      const diff = y - startY.current;

      // Only activate on downward pull
      if (diff < 10) {
        if (isPulling.current) {
          isPulling.current = false;
          setPulling(false);
          setPullDistance(0);
        }
        return;
      }

      // Allow pull if touch started in top-bar area (top 80px) OR chat is at scroll-top.
      // After reload messages load + auto-scroll to bottom, so isAtTop() is false —
      // checking startY lets the user always pull from the top bar.
      if (!isPulling.current && !isAtTop() && startY.current > 80) return;

      e.preventDefault();
      isPulling.current = true;
      currentY.current = y;

      const distance = Math.min(diff * RESISTANCE, MAX_PULL);
      setPulling(true);
      setPullDistance(distance);
    }

    function onTouchEnd() {
      if (!isPulling.current) return;
      isPulling.current = false;

      const distance = (currentY.current - startY.current) * RESISTANCE;

      if (distance >= THRESHOLD) {
        refreshingRef.current = true;
        setRefreshing(true);
        setPullDistance(THRESHOLD);
        setTimeout(() => {
          window.location.reload();
        }, 300);
      } else {
        setPulling(false);
        setPullDistance(0);
      }
    }

    document.addEventListener('touchstart', onTouchStart, { passive: true });
    document.addEventListener('touchmove', onTouchMove, { passive: false });
    document.addEventListener('touchend', onTouchEnd, { passive: true });

    return () => {
      document.removeEventListener('touchstart', onTouchStart);
      document.removeEventListener('touchmove', onTouchMove);
      document.removeEventListener('touchend', onTouchEnd);
    };
  }, []); // no deps — stable listeners, use refs for mutable state

  return { pulling, pullDistance, refreshing };
}
